import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, ArrowRight, FileSpreadsheet, Edit2, Check, X, ChevronDown, ChevronUp, Building2, ListChecks, CalendarIcon, Search, UserPlus } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function SmartImport({ user, onLogout }) {
  const [drafts, setDrafts] = useState([]);

  const DatePick = ({ value, onChange, placeholder }) => {
    const dateVal = value ? new Date(value + 'T00:00:00') : undefined;
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className={`h-8 w-full justify-start text-left text-xs font-normal px-2 ${!value ? 'text-muted-foreground' : ''}`}>
            <CalendarIcon size={12} className="mr-1 shrink-0" />
            {value ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : (placeholder || 'Select date')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateVal}
            onSelect={(d) => {
              if (d) {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                onChange(`${y}-${m}-${day}`);
              } else {
                onChange('');
              }
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    );
  };
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [moving, setMoving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [properties, setProperties] = useState([]);
  const [plans, setPlans] = useState([]);
  const [crmCustomers, setCrmCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickForm, setQuickForm] = useState({ customer_name: '', mobile_number: '', address: '', relation_type: '', relation_name: '' });

  const emptyForm = {
    customer_name: '', mobile_number: '', address: '', relation_type: '', relation_name: '',
    property_id: '', property_name: '', property_size: '', total_price: '', payment_type: 'Installment', booking_date: '',
    selected_plan: '',
    installments: [{ name: 'Booking Amount', amount: '', due_date: '', status: 'Paid', paid_amount: '', payment_mode: 'cash', payment_date: '' }]
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchDrafts();
    fetchProperties();
    fetchPlans();
    fetchCrmCustomers();
  }, []);

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/smart-import/drafts');
      setDrafts(res.data.filter(d => d.status !== 'moved'));
    } catch { toast.error('Failed to load drafts'); }
    finally { setLoading(false); }
  };

  const fetchProperties = async () => {
    try {
      const res = await api.get('/properties');
      setProperties((res.data || []).filter(p => p.property_name));
    } catch {}
  };

  const fetchPlans = async () => {
    try {
      const res = await api.get('/installment-plans');
      setPlans(res.data || []);
    } catch {}
  };

  const fetchCrmCustomers = async () => {
    try {
      const res = await api.get('/simple-customers');
      setCrmCustomers(res.data || []);
    } catch {}
  };

  const selectCustomer = (cust) => {
    setForm(f => ({
      ...f,
      customer_name: cust.customer_name || '',
      mobile_number: cust.mobile_number || '',
      address: cust.address || '',
      relation_type: cust.relation_type || '',
      relation_name: cust.relation_name || ''
    }));
    setCustomerSearch('');
    setShowCustomerDropdown(false);
    toast.success(`${cust.customer_name} selected!`);
  };

  const quickAddCustomer = async () => {
    if (!form.customer_name || !form.mobile_number) {
      toast.error('Customer name aur mobile daalein pehle');
      return;
    }
    setAddingCustomer(true);
    try {
      await api.post('/simple-customers', {
        customer_name: form.customer_name,
        mobile_number: form.mobile_number,
        address: form.address || '',
        relation_type: form.relation_type || '',
        relation_name: form.relation_name || ''
      });
      toast.success(`${form.customer_name} CRM mein add ho gaya!`);
      fetchCrmCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Customer add failed');
    } finally {
      setAddingCustomer(false);
    }
  };

  const filteredCrmCustomers = customerSearch.trim().length >= 2
    ? crmCustomers.filter(c =>
        (c.customer_name || '').toLowerCase().includes(customerSearch.toLowerCase()) ||
        (c.mobile_number || '').includes(customerSearch)
      ).slice(0, 8)
    : [];

  const handleQuickAddCustomer = async () => {
    if (!quickForm.customer_name || !quickForm.mobile_number) {
      toast.error('Customer name aur mobile number daalna zaroori hai');
      return;
    }
    setAddingCustomer(true);
    try {
      await api.post('/simple-customers', {
        customer_name: quickForm.customer_name,
        mobile_number: quickForm.mobile_number,
        address: quickForm.address || '',
        relation_type: quickForm.relation_type || '',
        relation_name: quickForm.relation_name || ''
      });
      toast.success(`${quickForm.customer_name} CRM mein add ho gaya!`);
      setQuickAddOpen(false);
      setQuickForm({ customer_name: '', mobile_number: '', address: '', relation_type: '', relation_name: '' });
      fetchCrmCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Customer add failed');
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.customer_name || !form.mobile_number) {
      toast.error('Customer name aur mobile number required hai');
      return;
    }
    try {
      if (editingDraft) {
        await api.put(`/smart-import/drafts/${editingDraft}`, form);
        toast.success('Draft updated!');
      } else {
        await api.post('/smart-import/add', form);
        toast.success('Draft added!');
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingDraft(null);
      fetchDrafts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const selectProperty = (propId) => {
    if (propId === '__manual__') {
      setForm(f => ({ ...f, property_id: '', property_name: '', property_size: '', total_price: '' }));
      return;
    }
    const prop = properties.find(p => p.id === propId);
    if (prop) {
      setForm(f => ({
        ...f, property_id: prop.id, property_name: prop.property_name,
        property_size: prop.size || '', total_price: prop.total_price || f.total_price
      }));
    }
  };

  const selectPlan = (planId) => {
    if (planId === '__none__') {
      setForm(f => ({ ...f, selected_plan: '' }));
      return;
    }
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;
    const totalPrice = parseFloat(form.total_price) || 0;
    const bookingDate = form.booking_date || new Date().toISOString().split('T')[0];

    const newInstallments = plan.installments.map((inst) => {
      let amount = 0;
      if (inst.amount && inst.amount > 0) {
        amount = inst.amount;
      } else if (inst.percentage && inst.percentage > 0 && totalPrice > 0) {
        amount = Math.round((inst.percentage / 100) * totalPrice);
      }
      let dueDate = bookingDate;
      if (inst.months_from_booking && inst.months_from_booking > 0 && bookingDate) {
        const d = new Date(bookingDate);
        d.setMonth(d.getMonth() + inst.months_from_booking);
        dueDate = d.toISOString().split('T')[0];
      }
      return {
        name: inst.installment_name, amount: amount || '',
        due_date: dueDate, status: 'Pending', paid_amount: '', payment_mode: 'cash', payment_date: ''
      };
    });
    setForm(f => ({ ...f, selected_plan: planId, installments: newInstallments }));
  };

  const addInstallment = () => {
    const num = form.installments.length + 1;
    setForm(f => ({
      ...f,
      installments: [...f.installments, { name: `${num === 2 ? '2nd' : num === 3 ? '3rd' : num + 'th'} Installment`, amount: '', due_date: '', status: 'Pending', paid_amount: '', payment_mode: 'cash', payment_date: '' }]
    }));
  };

  const removeInstallment = (idx) => {
    setForm(f => ({ ...f, installments: f.installments.filter((_, i) => i !== idx) }));
  };

  const updateInstallment = (idx, field, value) => {
    setForm(f => {
      const inst = [...f.installments];
      inst[idx] = { ...inst[idx], [field]: value };
      return { ...f, installments: inst };
    });
  };

  const openEdit = (draft) => {
    setEditingDraft(draft.id);
    setForm({
      customer_name: draft.customer_name, mobile_number: draft.mobile_number,
      address: draft.address || '', relation_type: draft.relation_type || '',
      relation_name: draft.relation_name || '', property_id: draft.property_id || '',
      property_name: draft.property_name || '',
      property_size: draft.property_size || '', total_price: draft.total_price || '',
      payment_type: draft.payment_type || 'Installment', booking_date: draft.booking_date || '',
      selected_plan: '',
      installments: (draft.installments || []).map(i => ({
        name: i.installment_name || i.name, amount: i.amount, due_date: (i.due_date || '').split('T')[0], status: i.status,
        paid_amount: i.paid_amount || '', payment_mode: i.payment_mode || 'cash', payment_date: (i.payment_date || i.paid_date || '').split('T')[0]
      }))
    });
    setDialogOpen(true);
  };

  const deleteDraft = async (id) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await api.delete(`/smart-import/drafts/${id}`);
      toast.success('Deleted!');
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      fetchDrafts();
    } catch { toast.error('Failed'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === drafts.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(drafts.map(d => d.id)));
  };

  const moveToBilling = async () => {
    const ids = selectedIds.size > 0 ? Array.from(selectedIds) : drafts.map(d => d.id);
    if (ids.length === 0) { toast.error('No drafts to move'); return; }
    if (!window.confirm(`${ids.length} entries ko billing mein move karein?`)) return;
    setMoving(true);
    try {
      const res = await api.post('/smart-import/move-to-billing', { ids });
      toast.success(`${res.data.moved} bookings billing mein move ho gaye! ${res.data.customers_created} new customers created.`);
      setSelectedIds(new Set());
      fetchDrafts();
    } catch (e) { toast.error('Move failed'); }
    finally { setMoving(false); }
  };

  const draftStats = {
    total: drafts.length,
    totalValue: drafts.reduce((s, d) => s + (d.total_price || 0), 0),
    totalPaid: drafts.reduce((s, d) => s + (d.total_paid || 0), 0),
    totalBalance: drafts.reduce((s, d) => s + (d.balance_due || 0), 0)
  };

  const instTotal = form.installments.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const instPaid = form.installments.reduce((s, i) => {
    if (i.status === 'Paid') return s + (parseFloat(i.paid_amount) || parseFloat(i.amount) || 0);
    if (i.status === 'Partially Paid') return s + (parseFloat(i.paid_amount) || 0);
    return s;
  }, 0);

  return (
    <Layout user={user} onLogout={onLogout} currentPage="smart-import">
      <div className="space-y-6" data-testid="smart-import-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Smart Import</h1>
            <p className="text-sm text-slate-500 mt-1">Customer + Billing direct add karein - billing mein tab jayega jab aap move karenge</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setQuickAddOpen(true)} className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="quick-add-customer-page-btn">
              <UserPlus size={16} /> Quick Add Customer
            </Button>
            {drafts.length > 0 && (
              <Button onClick={moveToBilling} disabled={moving} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="move-to-billing-btn">
                <ArrowRight size={16} />
                {moving ? 'Moving...' : `Move ${selectedIds.size > 0 ? selectedIds.size : 'All'} to Billing`}
              </Button>
            )}
            <Button onClick={() => { setEditingDraft(null); setForm(emptyForm); setDialogOpen(true); }} className="gap-2" data-testid="add-draft-btn">
              <Plus size={16} /> Add Entry
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{draftStats.total}</p>
            <p className="text-xs text-slate-500">Draft Entries</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-slate-700">₹{(draftStats.totalValue / 100000).toFixed(1)}L</p>
            <p className="text-xs text-slate-500">Total Value</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">₹{(draftStats.totalPaid / 100000).toFixed(1)}L</p>
            <p className="text-xs text-slate-500">Already Paid</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-red-600">₹{(draftStats.totalBalance / 100000).toFixed(1)}L</p>
            <p className="text-xs text-slate-500">Pending</p>
          </CardContent></Card>
        </div>

        {/* Drafts Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileSpreadsheet size={18} /> Staging Area
              <span className="text-xs font-normal text-slate-400 ml-2">Yeh data billing mein nahi hai abhi</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedIds.size > 0 && (
              <div className="flex items-center justify-between mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <span className="text-sm font-medium text-blue-700">{selectedIds.size} selected</span>
                <div className="flex gap-2">
                  <Button size="sm" onClick={moveToBilling} disabled={moving} className="gap-1 bg-emerald-600 hover:bg-emerald-700">
                    <ArrowRight size={14} /> Move Selected
                  </Button>
                  {user?.role === 'admin' && (
                  <Button size="sm" variant="destructive" onClick={async () => {
                    if (!window.confirm(`${selectedIds.size} drafts delete karein?`)) return;
                    await api.post('/smart-import/bulk-delete', { ids: Array.from(selectedIds) });
                    toast.success('Deleted!');
                    setSelectedIds(new Set());
                    fetchDrafts();
                  }}>
                    <Trash2 size={14} />
                  </Button>
                  )}
                </div>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <input type="checkbox" checked={drafts.length > 0 && selectedIds.size === drafts.length} onChange={toggleSelectAll} className="accent-emerald-600 w-4 h-4" />
                  </TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Paid</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
                ) : drafts.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-400">
                    <p className="text-lg mb-2">Koi draft nahi hai</p>
                    <p className="text-sm">"Add Entry" button se customer + billing add karein</p>
                  </TableCell></TableRow>
                ) : drafts.map(d => (
                  <TableRow key={d.id} className={`${selectedIds.has(d.id) ? 'bg-emerald-50' : ''} cursor-pointer`} onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(d.id)} onChange={() => toggleSelect(d.id)} className="accent-emerald-600 w-4 h-4" />
                    </TableCell>
                    <TableCell className="font-medium">{d.customer_name}
                      {d.relation_type && <span className="text-xs text-slate-400 block">{d.relation_type} {d.relation_name}</span>}
                    </TableCell>
                    <TableCell className="text-sm">{d.mobile_number}</TableCell>
                    <TableCell className="text-sm">{d.property_name}
                      {d.property_size && <span className="text-xs text-slate-400 block">{d.property_size}</span>}
                    </TableCell>
                    <TableCell className="font-semibold">₹{(d.total_price || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-emerald-600">₹{(d.total_paid || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-red-600">₹{(d.balance_due || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-sm">{(d.booking_date || '').split('T')[0]}</TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEdit(d)} data-testid={`edit-draft-${d.id}`}><Edit2 size={14} /></Button>
                        {user?.role === 'admin' && <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteDraft(d.id)}><Trash2 size={14} /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditingDraft(null); setForm(emptyForm); } }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="draft-dialog">
            <DialogHeader>
              <DialogTitle>{editingDraft ? 'Edit Draft' : 'Add New Entry'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4" onClick={() => setShowCustomerDropdown(false)}>
              {/* Customer Info */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-slate-600">Customer Details</p>

                {/* Customer Search */}
                <div className="relative">
                  <Label className="text-xs text-slate-500">Existing customer search karein ya neeche naya type karein</Label>
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                    <Input
                      value={customerSearch}
                      onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                      onFocus={() => setShowCustomerDropdown(true)}
                      placeholder="Name ya mobile se search karein..."
                      className="pl-8 text-sm"
                      data-testid="customer-search-smart-import"
                    />
                  </div>
                  {showCustomerDropdown && filteredCrmCustomers.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredCrmCustomers.map(c => (
                        <button
                          key={c.id}
                          onClick={() => selectCustomer(c)}
                          className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-slate-100 last:border-0 transition-colors"
                          data-testid={`select-customer-${c.id}`}
                        >
                          <span className="font-semibold text-sm text-slate-800">{c.customer_name}</span>
                          <span className="text-xs text-slate-500 ml-2">{c.mobile_number}</span>
                          {c.relation_type && <span className="text-xs text-slate-400 ml-1">({c.relation_type} {c.relation_name})</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {showCustomerDropdown && customerSearch.trim().length >= 2 && filteredCrmCustomers.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs text-slate-400">
                      Koi customer nahi mila — neeche details type karein
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Customer Name *</Label>
                    <Input value={form.customer_name} onChange={e => setForm(f => ({...f, customer_name: e.target.value}))} placeholder="Rajesh Kumar" data-testid="draft-customer-name" /></div>
                  <div><Label className="text-xs">Mobile *</Label>
                    <Input value={form.mobile_number} onChange={e => setForm(f => ({...f, mobile_number: e.target.value}))} placeholder="9876543210" data-testid="draft-mobile" /></div>
                  <div><Label className="text-xs">Address</Label>
                    <Input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} placeholder="City, State" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Relation</Label>
                      <Select value={form.relation_type} onValueChange={v => setForm(f => ({...f, relation_type: v}))}>
                        <SelectTrigger><SelectValue placeholder="S/O" /></SelectTrigger>
                        <SelectContent><SelectItem value="S/O">S/O</SelectItem><SelectItem value="W/O">W/O</SelectItem><SelectItem value="D/O">D/O</SelectItem><SelectItem value="C/O">C/O</SelectItem></SelectContent>
                      </Select></div>
                    <div><Label className="text-xs">Name</Label>
                      <Input value={form.relation_name} onChange={e => setForm(f => ({...f, relation_name: e.target.value}))} placeholder="Father/Spouse" /></div>
                  </div>
                </div>

                {/* Quick Add Customer to CRM - Prominent Bar */}
                {form.customer_name && form.mobile_number && (
                  <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-3 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-emerald-900 truncate">{form.customer_name}</p>
                      <p className="text-xs text-emerald-700">{form.mobile_number} {form.relation_type ? `| ${form.relation_type} ${form.relation_name}` : ''}</p>
                    </div>
                    <Button onClick={quickAddCustomer} disabled={addingCustomer} className="gap-2 bg-emerald-600 hover:bg-emerald-700 shrink-0 h-10 px-5 text-sm font-semibold" data-testid="quick-add-customer-btn">
                      <UserPlus size={18} />
                      {addingCustomer ? 'Adding...' : 'Customer CRM mein Add Karein'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Property & Booking Info */}
              <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-blue-600 flex items-center gap-2"><Building2 size={16} /> Booking Details</p>

                {/* Property Selector */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label className="text-xs">Select Property (ya manually type karein)</Label>
                    <Select value={form.property_id || '__manual__'} onValueChange={selectProperty}>
                      <SelectTrigger data-testid="property-select"><SelectValue placeholder="Property chunein..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__manual__">Manually Type Karein</SelectItem>
                        {properties.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.property_name} {p.size ? `(${p.size})` : ''} {p.total_price ? `- ₹${Number(p.total_price).toLocaleString('en-IN')}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><Label className="text-xs">Property Name *</Label>
                    <Input value={form.property_name} onChange={e => setForm(f => ({...f, property_name: e.target.value}))} placeholder="Plot A-101" data-testid="draft-property" /></div>
                  <div><Label className="text-xs">Size</Label>
                    <Input value={form.property_size} onChange={e => setForm(f => ({...f, property_size: e.target.value}))} placeholder="1200 sq ft" /></div>
                  <div><Label className="text-xs">Total Price *</Label>
                    <Input type="number" value={form.total_price} onChange={e => setForm(f => ({...f, total_price: e.target.value}))} placeholder="5000000" data-testid="draft-price" /></div>
                  <div><Label className="text-xs">Booking Date</Label>
                    <DatePick value={form.booking_date} onChange={v => setForm(f => ({...f, booking_date: v}))} placeholder="Booking date" /></div>
                </div>
                <div>
                  <Label className="text-xs">Payment Type</Label>
                  <Select value={form.payment_type} onValueChange={v => setForm(f => ({...f, payment_type: v}))}>
                    <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Installment">Installment</SelectItem><SelectItem value="Full Payment">Full Payment</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>

              {/* Installments */}
              {form.payment_type === 'Installment' && (
                <div className="bg-amber-50 rounded-xl p-4 space-y-3">
                  <div className="flex flex-wrap justify-between items-center gap-2">
                    <p className="text-sm font-semibold text-amber-700 flex items-center gap-2">
                      <ListChecks size={16} /> Installments ({form.installments.length})
                    </p>
                    <div className="flex gap-2">
                      {/* Plan Selector */}
                      {plans.length > 0 && (
                        <Select value={form.selected_plan || '__none__'} onValueChange={selectPlan}>
                          <SelectTrigger className="text-xs h-8 w-48" data-testid="plan-select">
                            <SelectValue placeholder="Plan se load karein" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Manual Entry</SelectItem>
                            {plans.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.plan_name} ({p.installments?.length || 0} installments)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Button size="sm" variant="outline" onClick={addInstallment} className="gap-1 text-xs">
                        <Plus size={12} /> Add
                      </Button>
                    </div>
                  </div>

                  {/* Column Headers */}
                  <div className="grid grid-cols-12 gap-1 px-2 text-[9px] font-semibold text-slate-400 uppercase tracking-wider">
                    <div className="col-span-2">Name</div>
                    <div className="col-span-2">Amount</div>
                    <div className="col-span-2">Due Date</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Paid Amt</div>
                    <div className="col-span-1">Pay Date</div>
                    <div className="col-span-1"></div>
                  </div>

                  <div className="space-y-2">
                    {form.installments.map((inst, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-2 space-y-1">
                        <div className="grid grid-cols-12 gap-1 items-center">
                          <div className="col-span-2">
                            <Input value={inst.name} onChange={e => updateInstallment(idx, 'name', e.target.value)} placeholder="Name" className="text-xs h-8" />
                          </div>
                          <div className="col-span-2">
                            <Input type="number" value={inst.amount} onChange={e => updateInstallment(idx, 'amount', e.target.value)} placeholder="Amount" className="text-xs h-8" />
                          </div>
                          <div className="col-span-2">
                            <DatePick value={inst.due_date} onChange={v => updateInstallment(idx, 'due_date', v)} placeholder="Due date" />
                          </div>
                          <div className="col-span-2">
                            <Select value={inst.status} onValueChange={v => {
                              updateInstallment(idx, 'status', v);
                              if (v === 'Paid') {
                                updateInstallment(idx, 'paid_amount', inst.amount || '');
                              } else if (v === 'Pending') {
                                updateInstallment(idx, 'paid_amount', '');
                                updateInstallment(idx, 'payment_date', '');
                              }
                            }}>
                              <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Paid">Paid</SelectItem>
                                <SelectItem value="Partially Paid">Partial</SelectItem>
                                <SelectItem value="Pending">Pending</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-2">
                            {(inst.status === 'Paid' || inst.status === 'Partially Paid') ? (
                              <Input type="number" value={inst.paid_amount} onChange={e => updateInstallment(idx, 'paid_amount', e.target.value)} placeholder="Paid amt" className="text-xs h-8" />
                            ) : <div />}
                          </div>
                          <div className="col-span-1">
                            {(inst.status === 'Paid' || inst.status === 'Partially Paid') ? (
                              <DatePick value={inst.payment_date || ''} onChange={v => updateInstallment(idx, 'payment_date', v)} placeholder="Pay date" />
                            ) : <div />}
                          </div>
                          <div className="col-span-1 text-center">
                            {form.installments.length > 1 && (
                              <Button size="sm" variant="ghost" onClick={() => removeInstallment(idx)} className="h-8 w-8 p-0 text-red-400 hover:text-red-600">
                                <X size={14} />
                              </Button>
                            )}
                          </div>
                        </div>
                        {(inst.status === 'Paid' || inst.status === 'Partially Paid') && (
                          <div className="flex items-center gap-2 pl-1">
                            <span className="text-[10px] text-slate-400">Mode:</span>
                            <Select value={inst.payment_mode || 'cash'} onValueChange={v => updateInstallment(idx, 'payment_mode', v)}>
                              <SelectTrigger className="text-[10px] h-6 w-24"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cash">Cash</SelectItem>
                                <SelectItem value="cheque">Cheque</SelectItem>
                                <SelectItem value="upi">UPI</SelectItem>
                                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    Total: ₹{instTotal.toLocaleString('en-IN')} | Paid: ₹{instPaid.toLocaleString('en-IN')}
                    {instTotal > 0 && parseFloat(form.total_price) > 0 && instTotal !== parseFloat(form.total_price) && (
                      <span className="text-red-500 ml-2">(Total Price se match nahi: ₹{parseFloat(form.total_price).toLocaleString('en-IN')})</span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setDialogOpen(false); setEditingDraft(null); setForm(emptyForm); }}>Cancel</Button>
                <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="save-draft-btn">
                  <Check size={16} /> {editingDraft ? 'Update Draft' : 'Save Draft'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Quick Add Customer Dialog */}
        <Dialog open={quickAddOpen} onOpenChange={setQuickAddOpen}>
          <DialogContent className="max-w-md" data-testid="quick-add-customer-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><UserPlus size={20} className="text-emerald-600" /> Quick Add Customer</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500 -mt-2">Customer CRM mein turant add hoga</p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Customer Name *</Label>
                <Input value={quickForm.customer_name} onChange={e => setQuickForm(f => ({...f, customer_name: e.target.value}))} placeholder="Rajesh Kumar" data-testid="quick-customer-name" />
              </div>
              <div>
                <Label className="text-xs">Mobile Number *</Label>
                <Input value={quickForm.mobile_number} onChange={e => setQuickForm(f => ({...f, mobile_number: e.target.value}))} placeholder="9876543210" data-testid="quick-customer-mobile" />
              </div>
              <div>
                <Label className="text-xs">Address</Label>
                <Input value={quickForm.address} onChange={e => setQuickForm(f => ({...f, address: e.target.value}))} placeholder="City, State" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Relation</Label>
                  <Select value={quickForm.relation_type} onValueChange={v => setQuickForm(f => ({...f, relation_type: v}))}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="S/O">S/O</SelectItem>
                      <SelectItem value="W/O">W/O</SelectItem>
                      <SelectItem value="D/O">D/O</SelectItem>
                      <SelectItem value="C/O">C/O</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Relation Name</Label>
                  <Input value={quickForm.relation_name} onChange={e => setQuickForm(f => ({...f, relation_name: e.target.value}))} placeholder="Father/Spouse" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setQuickAddOpen(false)}>Cancel</Button>
                <Button onClick={handleQuickAddCustomer} disabled={addingCustomer} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="quick-add-submit-btn">
                  <UserPlus size={16} />
                  {addingCustomer ? 'Adding...' : 'Add to CRM'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
